import { useEffect, useRef, useState } from 'react';

const ROLL_MS = 650;

/** ease-out cubic: fast off the old value, settling onto the new one */
const easeOut = (t: number) => 1 - (1 - t) ** 3;

/**
 * Rolls a displayed amount from its previous value to the next instead of swapping it, so a total
 * that changes while the shopper is looking at it reads as moving. The first render, reduced
 * motion and `enabled: false` all show the value as-is, and every roll lands on the exact value
 * passed in, never a rounded neighbour of it.
 */
export default function useAnimatedNumber(value: number, enabled = true): number {
  const [shown, setShown] = useState(value);
  // what is on screen right now, so a retarget mid-roll continues from there
  const shownRef = useRef(value);
  const frame = useRef<number | undefined>(undefined);

  useEffect(() => {
    const show = (next: number) => {
      shownRef.current = next;
      setShown(next);
    };
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const start = shownRef.current;
    if (!enabled || reduceMotion || start === value) {
      show(value);
      return;
    }

    const startedAt = performance.now();
    const step = (now: number) => {
      const progress = Math.min((now - startedAt) / ROLL_MS, 1);
      show(progress < 1 ? Math.round(start + (value - start) * easeOut(progress)) : value);
      if (progress < 1) frame.current = window.requestAnimationFrame(step);
    };
    frame.current = window.requestAnimationFrame(step);
    return () => {
      if (frame.current !== undefined) window.cancelAnimationFrame(frame.current);
    };
  }, [value, enabled]);

  return shown;
}
